import React from 'react';
import { trackEvent, BOOKING_SCREENING_URL, BOOKING_SUPERVISION_URL, EMAIL_ADDRESS } from '../lib/tracking';
import { optimizeImage } from '../lib/image';

export default function CorporatePsychotherapist() {
  return (
    <div className="bg-[#ECEEF1]">
      {/* Hero */}
      <section className="py-16 sm:py-24 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block">
                Erhverv · Erhverv & Organisationer
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#5A232E] tracking-tight leading-[1.12]">
                Erhvervspsykoterapeut
              </h1>
              <p className="text-base sm:text-lg text-[#6c7480] leading-relaxed">
                En erhvervspsykoterapeut har en fireårig psykoterapeutuddannelse, der er godkendt og evalueret af Dansk Psykoterapeutforening eller tilsvarende. Erhvervspsykoterapeuten kombinerer psykoterapeutiske teorier og metoder med relevant erhvervserfaring og en forudgående uddannelse.
              </p>
              <p className="text-sm sm:text-base text-[#11161d] leading-relaxed">
                Erhvervspsykoterapi kan blandt andet bruges til at støtte individuelle ledere, medarbejdere, teams og organisationer i arbejdet med komplekse udfordringer, arbejdsmiljø, samarbejde, trivsel og udvikling. Kim kombinerer på denne måde psykoterapeutiske kompetencer med solid erhvervsmæssig erfaring.
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <a
                  href={BOOKING_SCREENING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('cta_booking_click', { location: 'corporate_psychotherapist_hero' })}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm shadow-md transition-all no-break whitespace-nowrap"
                >
                  <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                  <span>Book en gratis afklaringssamtale</span>
                </a>
                <a
                  href={`mailto:${EMAIL_ADDRESS}?subject=Forespørgsel vedrørende erhvervspsykoterapi`}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#ECEEF1] text-[#5A232E] font-medium text-sm border border-[#D9DDE2] hover:bg-[#D9DDE2] transition-colors no-break whitespace-nowrap"
                >
                  <i className="fa-solid fa-envelope" aria-hidden="true" />
                  <span>Kontakt Kim for en dialog</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-[#D9DDE2]">
                <img
                  src={optimizeImage('kim-outdoor-building.webp', 800)}
                  alt="Erhvervspsykoterapeut Kim Palasz"
                  className="w-full h-auto object-cover max-h-[440px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Kernekompetencer */}
      <section className="py-16 sm:py-20 bg-[#ECEEF1] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-2">
              Faglige styrker
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
              Kernekompetencer
            </h2>
            <p className="text-sm sm:text-base text-[#6c7480] mt-3">
              Kombinationen af dyb psykologisk indsigt og erhvervsforståelse skaber et stærkt fundament:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: 'Menneskelig & organisatorisk forståelse',
                desc: 'Evnen til at forstå, hvordan mennesker oplever sig selv og andre, samt hvordan en organisations kultur påvirker trivsel og samarbejde.',
                icon: 'fa-solid fa-users',
              },
              {
                title: 'Analyse af relationelle dynamikker',
                desc: 'Analyse af komplekse problemstillinger i relationer mellem mennesker og i organisationen.',
                icon: 'fa-solid fa-diagram-project',
              },
              {
                title: 'Støtte til ledere & selvindsigt',
                desc: 'Støtte til ledere i at få nye indsigter, forstå egne mønstre og opnå større forståelse for andre menneskers adfærd og motivation.',
                icon: 'fa-solid fa-lightbulb',
              },
              {
                title: 'Læring & forandringsprocesser',
                desc: 'Facilitering af læring og forandring gennem psykologiske processer og en holistisk og fænomenologisk tilgang.',
                icon: 'fa-solid fa-seedling',
              },
              {
                title: 'Trygt & tillidsfuldt rum',
                desc: 'Empatisk og fordomsfri lytning og etablering af et trygt og tillidsfuldt rum for udvikling.',
                icon: 'fa-solid fa-shield-heart',
              },
              {
                title: 'Psykologisk tryghed & supervision',
                desc: 'Målrettet arbejde med psykologisk tryghed i organisationer samt professionel supervision af teams og ledergrupper.',
                icon: 'fa-solid fa-comments',
              },
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-7 rounded-2xl border border-[#D9DDE2] shadow-2xs">
                <div className="w-10 h-10 rounded-xl bg-[#5A232E]/10 text-[#5A232E] flex items-center justify-center text-sm mb-4">
                  <i className={item.icon} aria-hidden="true" />
                </div>
                <h3 className="font-display text-lg text-[#5A232E] mb-2">{item.title}</h3>
                <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hjælp til ledere og ledergrupper */}
      <section className="py-16 sm:py-20 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-2">
              Ledelse & Samarbejde
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
              Hjælp til ledere og ledergrupper
            </h2>
            <p className="text-sm sm:text-base text-[#6c7480] mt-3">
              Målrettet støtte til at styrke lederskab, beslutningskraft og trivsel i ledelsen:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-[#ECEEF1] p-6 rounded-2xl border border-[#D9DDE2]">
              <h3 className="font-display text-lg text-[#5A232E] mb-2">Stresshåndtering</h3>
              <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                Teknikker og strategier til at reducere stressniveauer og skabe bæredygtig balance i et krævende lederjob.
              </p>
            </div>
            <div className="bg-[#ECEEF1] p-6 rounded-2xl border border-[#D9DDE2]">
              <h3 className="font-display text-lg text-[#5A232E] mb-2">Konflikthåndtering</h3>
              <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                Identifikation, forståelse og konstruktiv løsning af konflikter i arbejdsgrupper eller mellem ledere og medarbejdere.
              </p>
            </div>
            <div className="bg-[#ECEEF1] p-6 rounded-2xl border border-[#D9DDE2]">
              <h3 className="font-display text-lg text-[#5A232E] mb-2">Teamudvikling</h3>
              <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                Styrkelse af samarbejdsrelationer, teamwork og åben, assertiv kommunikation i teams.
              </p>
            </div>
            <div className="bg-[#ECEEF1] p-6 rounded-2xl border border-[#D9DDE2]">
              <h3 className="font-display text-lg text-[#5A232E] mb-2">Ledelsesudvikling</h3>
              <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                Individuel coaching og udvikling af ledelsesmæssige kompetencer, herunder kommunikation, beslutningstagning og konflikthåndtering.
              </p>
            </div>
            <div className="bg-[#ECEEF1] p-6 rounded-2xl border border-[#D9DDE2]">
              <h3 className="font-display text-lg text-[#5A232E] mb-2">Arbejdspladskultur</h3>
              <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                Arbejde med en sund og positiv kultur, trivsel, inklusion, diversitet og balance på arbejdspladsen.
              </p>
            </div>
            <div className="bg-[#ECEEF1] p-6 rounded-2xl border border-[#D9DDE2]">
              <h3 className="font-display text-lg text-[#5A232E] mb-2">Supervision</h3>
              <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                Supervision for teams og ledergrupper med fokus på større gruppeforståelse, faglig refleksion og udvikling.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hjælp til medarbejdere */}
      <section className="py-16 sm:py-20 bg-[#ECEEF1] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E]">
                Individuel Støtte
              </span>
              <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
                Hjælp til medarbejdere
              </h2>
              <p className="text-sm sm:text-base text-[#11161d] leading-relaxed">
                Erhvervspsykoterapi yder fortrolig støtte til medarbejdere til at håndtere arbejdsrelaterede udfordringer, tilpasse sig forandringer og udvikle sunde strategier for personlig trivsel og arbejdsliv.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 text-xs sm:text-sm text-[#11161d]">
                {[
                  'Stresshåndtering',
                  'Angst og depression',
                  'Arbejdsrelaterede konflikter',
                  'Work-life balance',
                  'Karriererådgivning',
                  'Selvudvikling',
                  'Forandringshåndtering',
                  'Kommunikationsfærdigheder',
                  'Trivsel og selvomsorg',
                  'Krisehåndtering',
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2.5 rounded-xl bg-white border border-[#D9DDE2]">
                    <i className="fa-solid fa-circle-check text-emerald-600 text-xs shrink-0" aria-hidden="true" />
                    <span className="font-medium text-[#11161d]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white p-8 rounded-3xl border border-[#D9DDE2] shadow-sm space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-[#5A232E] text-white flex items-center justify-center text-xl mb-4">
                  <i className="fa-solid fa-hand-holding-heart" aria-hidden="true" />
                </div>
                <h3 className="font-display text-2xl text-[#5A232E]">
                  Trygge rammer for medarbejderen
                </h3>
                <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                  Gennem et professionelt og fortroligt rum hjælpes medarbejdere til at genfinde overskud og motivation, inden mistrivsel udvikler sig til langvarig sygemelding.
                </p>
                <div className="pt-2">
                  <a
                    href={BOOKING_SCREENING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs font-semibold text-[#5A232E] hover:underline"
                  >
                    <span>Læs om forløb & afklaringssamtale →</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hjælp til organisationen */}
      <section className="py-16 sm:py-20 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-2">
              Struktur & Trivsel
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
              Hjælp til organisationen
            </h2>
            <p className="text-sm sm:text-base text-[#6c7480] mt-3">
              Arbejde med de overordnede organisatoriske problemstillinger, der skaber et sundt arbejdsmiljø:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-[#ECEEF1] p-6 rounded-2xl border border-[#D9DDE2]">
              <h3 className="font-display text-lg text-[#5A232E] mb-2">Forandringsledelse</h3>
              <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                Støtte til medarbejdere og ledere i forbindelse med organisatoriske ændringer, fusioner eller nye strukturer.
              </p>
            </div>
            <div className="bg-[#ECEEF1] p-6 rounded-2xl border border-[#D9DDE2]">
              <h3 className="font-display text-lg text-[#5A232E] mb-2">Medarbejderengagement</h3>
              <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                Identifikation af årsager til lavt engagement og udvikling af strategier til at styrke motivation og arbejdsglæde.
              </p>
            </div>
            <div className="bg-[#ECEEF1] p-6 rounded-2xl border border-[#D9DDE2]">
              <h3 className="font-display text-lg text-[#5A232E] mb-2">Stress og udbrændthed</h3>
              <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                Forebyggelse og håndtering af stress og udbrændthed i et strategisk og organisatorisk perspektiv.
              </p>
            </div>
            <div className="bg-[#ECEEF1] p-6 rounded-2xl border border-[#D9DDE2]">
              <h3 className="font-display text-lg text-[#5A232E] mb-2">Effektiv kommunikation</h3>
              <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                Forbedring af kommunikationsstrukturer og dialog på tværs af afdelinger og ledelseslag.
              </p>
            </div>
            <div className="bg-[#ECEEF1] p-6 rounded-2xl border border-[#D9DDE2]">
              <h3 className="font-display text-lg text-[#5A232E] mb-2">Konfliktløsning</h3>
              <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                Konstruktiv håndtering af konflikter for at beskytte arbejdsmiljøet og genskabe et stærkt samarbejde.
              </p>
            </div>
            <div className="bg-[#ECEEF1] p-6 rounded-2xl border border-[#D9DDE2]">
              <h3 className="font-display text-lg text-[#5A232E] mb-2">Psykologisk tryghed</h3>
              <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                Kulturskabelse hvor medarbejdere tør ytre sig, begå fejl og dele idéer uden frygt for sanktioner.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Terapeutisk tilgang */}
      <section className="py-16 sm:py-20 bg-[#ECEEF1] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E]">
              Metoder & Teori
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
              Terapeutisk tilgang
            </h2>
            <p className="text-sm sm:text-base text-[#11161d] leading-relaxed">
              Erhvervspsykoterapeuten anvender en bred palette af terapeutiske metoder, der tilpasses organisationens, teamets og individets konkrete udfordringer og behov.
            </p>
            <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
              Tilgangen integrerer blandt andet <strong>kognitiv adfærdsterapi</strong>, <strong>narrativ terapi</strong> og <strong>systemisk terapi</strong> som afsæt for at skabe indsigt, handlekraft og varige adfærdsændringer.
            </p>
          </div>
        </div>
      </section>

      {/* Resultater & Afsluttende CTA */}
      <section className="py-20 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <div className="max-w-3xl mx-auto">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-3">
              Mål & Udbytte
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E] tracking-tight">
              Målet med erhvervspsykoterapi
            </h2>
            <p className="mt-4 text-base text-[#6c7480] leading-relaxed">
              Fokus er altid på at skabe et trygt miljø, bedre trivsel, sunde og varige forandringer, stærkere samarbejde samt større forståelse mellem mennesker – til gavn for både medarbejdernes velbefindende og organisationens bæredygtige udvikling.
            </p>
          </div>

          <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={BOOKING_SCREENING_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('cta_booking_click', { location: 'corporate_psychotherapist_bottom' })}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-base shadow-lg transition-all no-break whitespace-nowrap"
            >
              <i className="fa-solid fa-calendar-check" aria-hidden="true" />
              <span>Book en gratis afklaringssamtale</span>
            </a>
            <a
              href={`mailto:${EMAIL_ADDRESS}?subject=Forespørgsel om erhvervspsykoterapi`}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-[#ECEEF1] hover:bg-[#D9DDE2] text-[#5A232E] font-medium text-base border border-[#D9DDE2] transition-colors no-break whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope" aria-hidden="true" />
              <span>Kontakt for et uforpligtende tilbud</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
